import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Globe, ArrowLeft, AlertTriangle, Scale, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Navigation */}
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-home-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold gradient-text">RouteFlow AI</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card className="border-0 shadow-lg" data-testid="terms-of-service-card">
          <CardHeader>
            <CardTitle className="text-3xl">Terms of Service</CardTitle>
            <p className="text-gray-600">Last updated: January 2025</p>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none space-y-6">
            
            {/* IMPORTANT LEGAL NOTICE - HIGHLIGHTED */}
            <section className="bg-red-50 border-2 border-red-500 p-6 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold mb-3 text-red-800">⚠️ IMPORTANT LEGAL NOTICE - PLEASE READ CAREFULLY</h2>
                  <p className="text-gray-800 font-semibold mb-2">
                    BY USING THIS SERVICE, YOU ACKNOWLEDGE AND AGREE THAT:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2">
                    <li><strong>RouteFlow AI, LLC is NOT a financial services provider.</strong></li>
                    <li><strong>We do NOT hold, custody, control, or have access to any user funds.</strong></li>
                    <li><strong>All financial transactions are performed EXCLUSIVELY by Guardarian OÜ.</strong></li>
                    <li><strong>You are entering into a direct contract with Guardarian OÜ for all financial services.</strong></li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">1. Agreement to Terms</h2>
              <p className="text-gray-700">
                By accessing or using RouteFlow AI, you agree to be bound by these Terms of Service and all applicable laws and regulations. 
                If you do not agree with any of these terms, you are prohibited from using this platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">2. Description of Service</h2>
              <p className="text-gray-700">
                RouteFlow AI is a <strong>technology platform and software interface only</strong>. We provide route optimization algorithms, 
                AI-assisted navigation, and a user interface to connect users with third-party licensed financial service providers. 
                <strong>RouteFlow AI does not provide any financial services directly.</strong>
              </p>
            </section>

            {/* NO-TOUCH CLAUSE - AGGRESSIVE VERSION */}
            <section className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <div className="flex items-start gap-3">
                <Shield className="w-8 h-8 text-amber-600 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-bold mb-3 text-amber-800">3. TECHNOLOGY PROVIDER DISCLAIMER ("NO-TOUCH" CLAUSE)</h2>
                  <p className="text-gray-800 font-bold mb-4 text-lg">
                    ROUTEFLOW AI, LLC IS SOLELY A TECHNOLOGY PROVIDER AND SOFTWARE INTERFACE. IT IS NOT A MONEY SERVICES BUSINESS (MSB), DOES NOT HOLD USER FUNDS, AND DOES NOT EXECUTE CURRENCY EXCHANGES.
                  </p>
                  <p className="text-gray-700 mb-3">RouteFlow AI, LLC expressly and unequivocally:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
                    <li><strong>IS NOT</strong> a Money Services Business (MSB) under any jurisdiction</li>
                    <li><strong>IS NOT</strong> a Money Transmitter, Payment Processor, or Financial Institution</li>
                    <li><strong>IS NOT</strong> a bank, credit union, or any regulated financial entity</li>
                    <li><strong>IS NOT</strong> a virtual currency exchange or cryptocurrency trading platform</li>
                    <li><strong>DOES NOT</strong> hold, custody, possess, or control user funds at any time</li>
                    <li><strong>DOES NOT</strong> execute, process, or facilitate currency exchanges or financial transactions</li>
                    <li><strong>DOES NOT</strong> have access to, control over, or possession of any cryptocurrency, fiat currency, or digital assets</li>
                    <li><strong>DOES NOT</strong> provide financial advice, investment advice, or tax advice</li>
                    <li><strong>DOES NOT</strong> guarantee, insure, or underwrite any transactions</li>
                  </ul>
                  <p className="text-gray-700 font-semibold">
                    RouteFlow AI provides ONLY the software interface, routing algorithms, and user experience layer that enables users to interact with third-party licensed financial service providers who perform all actual financial operations.
                  </p>
                </div>
              </div>
            </section>

            {/* AGENCY CLAUSE - AGGRESSIVE VERSION */}
            <section className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <div className="flex items-start gap-3">
                <Scale className="w-8 h-8 text-blue-600 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-bold mb-3 text-blue-800">4. THIRD-PARTY FINANCIAL SERVICES ("AGENCY" CLAUSE)</h2>
                  <p className="text-gray-800 font-bold mb-4 text-lg">
                    ALL FINANCIAL TRANSACTIONS, CURRENCY EXCHANGES, AND MONEY TRANSFERS ARE PERFORMED EXCLUSIVELY BY GUARDARIAN OÜ, A LICENSED VIRTUAL CURRENCY SERVICE PROVIDER. BY USING ROUTEFLOW AI, YOU ARE ENTERING INTO A DIRECT CONTRACTUAL RELATIONSHIP WITH GUARDARIAN OÜ.
                  </p>
                  <p className="text-gray-700 mb-3">By using RouteFlow AI, you explicitly acknowledge, understand, and agree that:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
                    <li>You are entering into a <strong>DIRECT contractual relationship with Guardarian OÜ</strong> (License No. FVT000200, regulated under Estonian law) for all financial services</li>
                    <li><strong>Guardarian OÜ is solely and exclusively responsible</strong> for executing all currency exchanges, money transfers, and financial transactions</li>
                    <li>Guardarian OÜ handles <strong>ALL</strong> KYC (Know Your Customer), AML (Anti-Money Laundering), and regulatory compliance requirements</li>
                    <li>Guardarian OÜ holds all necessary licenses and regulatory approvals to conduct financial services</li>
                    <li>RouteFlow AI acts <strong>ONLY</strong> as a technology intermediary providing the user interface and routing optimization</li>
                    <li><strong>ALL disputes, claims, and complaints regarding financial transactions must be directed exclusively to Guardarian OÜ</strong></li>
                    <li>RouteFlow AI has <strong>NO liability</strong> for any actions, omissions, or services provided by Guardarian OÜ</li>
                  </ul>
                  <p className="text-gray-700 mb-3">
                    <strong>Guardarian OÜ's Terms of Service and Privacy Policy govern all financial transactions.</strong> 
                    You are required to review and accept their terms at: 
                    <a href="https://guardarian.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">https://guardarian.com/terms</a>
                  </p>
                  <p className="text-gray-700 font-semibold bg-blue-100 p-3 rounded">
                    By initiating any transfer through RouteFlow AI, you confirm that you have read, understood, and agreed to Guardarian OÜ's terms and that you are contracting directly with Guardarian OÜ for the financial service.
                  </p>
                </div>
              </div>
            </section>

            {/* LIMITATION OF LIABILITY - DELAWARE STYLE */}
            <section className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
              <h2 className="text-2xl font-bold mb-3 text-purple-800">5. LIMITATION OF LIABILITY</h2>
              <div className="bg-purple-100 p-4 rounded-lg mb-4">
                <p className="text-gray-800 font-bold text-sm uppercase tracking-wide">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ROUTEFLOW AI, LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AFFILIATES, SUCCESSORS, OR ASSIGNS BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO THE USE OF, OR INABILITY TO USE, THIS SERVICE.
                </p>
              </div>
              <div className="bg-purple-200 p-4 rounded-lg mb-4">
                <p className="text-gray-800 font-bold">
                  OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIMS UNDER THESE TERMS, OR OTHERWISE IN CONNECTION WITH THE SERVICE, IS LIMITED TO THE LESSER OF: (A) THE NET AMOUNT OF FEES ACTUALLY PAID BY YOU DIRECTLY TO ROUTEFLOW AI, LLC (NOT TO ANY THIRD PARTY) DURING THE THREE (3) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE LIABILITY, OR (B) FIFTY UNITED STATES DOLLARS ($50.00 USD), WHICHEVER IS GREATER.
                </p>
              </div>
              <p className="text-gray-700">
                This limitation applies regardless of the legal theory upon which any claim is based, including breach of contract, tort (including negligence), strict liability, or any other theory, and whether or not RouteFlow AI, LLC has been advised of the possibility of such damages.
              </p>
              <p className="text-gray-700 mt-3">
                Some jurisdictions do not allow the exclusion or limitation of liability for consequential or incidental damages, so the above limitations may not apply to you. In such jurisdictions, our liability shall be limited to the greatest extent permitted by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">6. Non-Custodial Service</h2>
              <p className="text-gray-700">
                RouteFlow AI is a non-custodial service. We do not store, hold, or have access to your wallet, private keys, or funds. 
                You maintain full control and responsibility for your own financial accounts. 
                We cannot access, move, freeze, or recover your funds under any circumstances.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">7. User Responsibilities</h2>
              <p className="text-gray-700 mb-2">You agree to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Comply with all applicable laws and regulations in your jurisdiction</li>
                <li>Not use the service for illegal activities, money laundering, or terrorist financing</li>
                <li>Be solely responsible for your financial accounts and any third-party service agreements</li>
                <li>Review and accept Guardarian OÜ's terms before initiating any transaction</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">8. Transaction Fees</h2>
              <p className="text-gray-700">
                RouteFlow AI charges a 0.48% service fee for using our technology platform. This fee is collected by our partner Guardarian OÜ as part of the transaction processing. 
                Additional fees charged by Guardarian OÜ (payment processing, network fees, etc.) are set by Guardarian OÜ and are not controlled by RouteFlow AI. 
                All fees will be displayed by Guardarian OÜ before you confirm a transaction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">9. AI Assistant Limitations</h2>
              <p className="text-gray-700">
                Our AI assistant provides suggestions and route optimization based on available data. The AI's recommendations are for informational purposes only and 
                <strong> do not constitute financial advice, investment advice, or any professional advice</strong>. 
                You are solely responsible for reviewing and confirming all transaction details before proceeding.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">10. Transaction Risks</h2>
              <p className="text-gray-700 mb-2">You acknowledge and accept the following risks:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Blockchain transactions are irreversible once confirmed</li>
                <li>Cryptocurrency values can be highly volatile</li>
                <li>Network delays may affect transaction times</li>
                <li>Errors in recipient details may result in permanent loss of funds</li>
                <li>Regulatory changes may affect service availability</li>
                <li>Third-party service provider policies may change without notice</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">11. Service Availability</h2>
              <p className="text-gray-700">
                We strive to maintain continuous service availability but do not guarantee uninterrupted access. 
                We may suspend or terminate the service for maintenance, updates, or unforeseen circumstances without prior notice. 
                Service availability is also dependent on third-party providers including Guardarian OÜ.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">12. Indemnification</h2>
              <p className="text-gray-700">
                You agree to indemnify, defend, and hold harmless RouteFlow AI, LLC, its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses arising from: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any third-party rights; or (d) your transactions with Guardarian OÜ or any other third party.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">13. Compliance and KYC</h2>
              <p className="text-gray-700">
                All KYC (Know Your Customer) and AML (Anti-Money Laundering) compliance is handled exclusively by Guardarian OÜ. 
                RouteFlow AI does not collect or verify identity documents. 
                Guardarian OÜ reserves the right to request additional verification and to refuse service based on their compliance requirements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">14. Account Termination</h2>
              <p className="text-gray-700">
                We reserve the right to suspend or terminate your access to RouteFlow AI at any time for violation of these terms, 
                suspicious activity, or as required by law. Upon termination, you remain responsible for any pending obligations with third-party providers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">15. Intellectual Property</h2>
              <p className="text-gray-700">
                All content, features, and functionality of RouteFlow AI, including but not limited to our AI routing algorithms, user interface, and branding, are owned by RouteFlow AI, LLC and are protected by international copyright, 
                trademark, and other intellectual property laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">16. Dispute Resolution and Arbitration</h2>
              <p className="text-gray-700 mb-2">
                Any disputes arising from these terms shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, 
                conducted in New Castle County, Delaware, USA. 
              </p>
              <p className="text-gray-700 font-semibold">
                YOU WAIVE YOUR RIGHT TO PARTICIPATE IN CLASS ACTION LAWSUITS OR CLASS-WIDE ARBITRATION.
              </p>
              <p className="text-gray-700 mt-2">
                <strong>For disputes regarding financial transactions, you must contact Guardarian OÜ directly.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">17. Governing Law</h2>
              <p className="text-gray-700">
                These Terms of Service shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">18. Severability</h2>
              <p className="text-gray-700">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">19. Changes to Terms</h2>
              <p className="text-gray-700">
                We may modify these terms at any time. Continued use of the service after changes constitutes acceptance of the modified terms. 
                Material changes will be communicated via email or platform notification.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">20. Contact Information</h2>
              <p className="text-gray-700">
                For questions about these Terms of Service, please contact us at:
              </p>
              <div className="text-gray-700 mt-3 space-y-1">
                <p className="font-semibold">RouteFlow AI, LLC</p>
                <p>131 Continental Dr, Suite 305</p>
                <p>Newark, DE 19713, United States</p>
                <p className="mt-2">Email: <a href="mailto:legal@routeflowai.com" className="text-emerald-600 hover:underline">legal@routeflowai.com</a></p>
                <p>General Inquiries: <a href="mailto:info@routeflowai.com" className="text-emerald-600 hover:underline">info@routeflowai.com</a></p>
                <p>Phone: <a href="tel:+13025202688" className="text-emerald-600 hover:underline">+1 (302) 520-2688</a></p>
                <p>Website: <a href="https://www.routeflowai.com" className="text-emerald-600 hover:underline">www.routeflowai.com</a></p>
              </div>
            </section>

            {/* Final Acknowledgment */}
            <section className="bg-gray-100 border-2 border-gray-400 p-6 rounded-lg mt-8">
              <h2 className="text-xl font-bold mb-3 text-gray-800">ACKNOWLEDGMENT</h2>
              <p className="text-gray-700">
                BY CREATING AN ACCOUNT OR USING ROUTEFLOW AI, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE, INCLUDING THE TECHNOLOGY PROVIDER DISCLAIMER, AGENCY CLAUSE, AND LIMITATION OF LIABILITY PROVISIONS. YOU FURTHER ACKNOWLEDGE THAT ROUTEFLOW AI IS A TECHNOLOGY PLATFORM ONLY AND THAT ALL FINANCIAL SERVICES ARE PROVIDED BY GUARDARIAN OÜ.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
